import { PlusOutlined } from '@ant-design/icons';
import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { ColorPickerInput } from '@framework/form/inputs/ColorPickerInput.tsx';
import { colorPickerPresets } from '@framework/form/inputs/colorPickerPresets.ts';
import { NumberInput } from '@framework/form/inputs/NumberInput.tsx';
import { SliderNumberInput } from '@framework/form/inputs/SliderNumberInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { DrawerButtonRow } from '@framework/ui/components/drawer/DrawerButtonRow.tsx';
import { Button, message, Space } from 'antd';
import { MapLayerType } from '../../layers/MapLayer.ts';
import { type IMapPrintImage } from './MapPrintImage.ts';
import { MapPrintImageLayersSection } from './MapPrintImageLayersSection.tsx';
import { MapPrintImageSchema } from './MapPrintImageSchema.tsx';
import { MapPrintImageStylePreview } from './MapPrintImageStylePreview.tsx';

export interface IMapPrintImageConfigDrawerButtonProps {
  editor: RelationEditor<IMapPrintImage>;
}

export function MapPrintImageConfigDrawerButton(
  props: IMapPrintImageConfigDrawerButtonProps,
) {
  const { editor } = props;
  return (
    <div>
      <Button
        type="dashed"
        block
        size="large"
        icon={<PlusOutlined aria-hidden="true" />}
        onClick={e => {
          editor.handle.open(MapPrintImageSchema.cast({}, { assert: false }));
          e.stopPropagation();
        }}
      >
        Aggiungi Immagine in Stampa
      </Button>
      {/* Modal Drawer  */}
      <Drawer
        title={
          editor.handle.isNew
            ? 'Crea Immagine in Stampa'
            : 'Modifica Immagine in Stampa'
        }
        size="large"
        placement="right"
        onClose={editor.handle.cancel}
        open={editor.handle.isVisible}
      >
        <FormHandler
          nested
          layout="vertical"
          schema={MapPrintImageSchema}
          initialValues={
            editor.handle.current ??
            MapPrintImageSchema.cast({}, { assert: false })
          }
          onSubmit={async (values, e) => {
            try {
              const type = editor.handle.apply(values);
              if (type === 'updated') {
                message.success('Immagine di Stampa modificata');
              } else {
                message.success('Immagine di Stampa aggiunta');
              }
            } catch (e) {
              message.error(
                "Si è verificato un errore nella modifica dell'immagine",
              );
              console.error(e);
            }
          }}
        >
          <FormErrorsAlert />
          <FormItem name="name" label="Nome" help="Riferimento ad uso interno">
            <TextInput name="name" />
          </FormItem>

          <MapPrintImageLayersSection
            allowedTypes={[
              MapLayerType.ArcGISRestTile,
              MapLayerType.XYZ,
              MapLayerType.WMS,
              MapLayerType.OSM,
            ]}
          />

          <FormItem
            label="Padding"
            name={`padding`}
            help="Spazio di margine intorno alle geometrie"
          >
            <NumberInput min={0} name={`padding`} />
          </FormItem>

          <FormItem label="Colore di riempimento" name={`fillColor`}>
            <ColorPickerInput name={`fillColor`} presets={colorPickerPresets} />
          </FormItem>
          <FormItem
            label="Opacità del riempimento"
            name={`fillOpacity`}
            help="Un valore pari a 0 renderizza il riempimento completamente trasparente, un valore pari a 1 completamente opaco"
          >
            <SliderNumberInput
              min={0}
              max={1}
              step={0.1}
              precision={2}
              name={`fillOpacity`}
            />
          </FormItem>
          <FormItem label="Colore del bordo" name={`strokeColor`}>
            <ColorPickerInput
              name={`strokeColor`}
              presets={colorPickerPresets}
            />
          </FormItem>
          <FormItem label="Spessore del bordo" name={`strokeWidth`}>
            <SliderNumberInput
              min={0}
              max={10}
              step={1}
              precision={0}
              unit="px"
              name={`strokeWidth`}
            />
          </FormItem>
          <FormItem
            label="Opacità del bordo"
            name={`strokeOpacity`}
            help="Un valore pari a 0 renderizza il bordo completamente trasparente, un valore pari a 1 completamente opaco"
          >
            <SliderNumberInput
              min={0}
              max={1}
              step={0.1}
              precision={2}
              name={`strokeOpacity`}
            />
          </FormItem>

          <MapPrintImageStylePreview />

          <DrawerButtonRow>
            <Space>
              <Button size="large" onClick={editor.handle.cancel}>
                Annulla
              </Button>
              <Button type="primary" htmlType="submit" size="large">
                Applica
              </Button>
            </Space>
          </DrawerButtonRow>
        </FormHandler>
      </Drawer>
    </div>
  );
}
